import { CandidateStatus } from "@/services/DTOs/Candidate";
import { CandidateContext } from "@/services/DTOs/CandidateContext";
import { IPersonSummary, PersonSummary } from "@/services/DTOs/PersonSummary";
import { IOpportunitiesEvalResume, IPeopleEvalResult } from "@/services/DTOs/search/SearchResult";
import { CandidateAdvisor, ICandidateAdvisor } from "./CandidateAdvisor";
import { CandidateDeliveryDeadline } from "./CandidateDeliveryDeadline";
import { Compensation, ICompensation } from "./Compensation";
import { ICandidateApplicationStep } from "./EditApplicationStep";

export type CandidateState = "suggested_from_torre" | "suggested_from_database" | "invited"  | "interested" |
  "matched" | "disqualified" | "retracted";

export enum MatchStatus {
  MATCH = "match",
  NO_MATCH = "no_match",
  UNKNOWN = "unknown"
}

export interface ICandidateSummaryByCategories {
  [name: string]: ICandidateSummaryCategory;
}

export interface ICandidateSummaryCategory {
  categoryName: string;
  candidates: CandidateSummary[];
  currentCandidatesPage: number;
  totalCandidates: number;
  candidateStatus: CandidateStatus[];
  matchAndRankStatus: string;
  isMatched?: boolean;
  isHidden?: boolean;
  matchStatus?: MatchStatus[];
  expanded: boolean;
  loading: boolean;
  loadingMore: boolean;
}

export interface ICandidateSearchResult {
  id: string;
  person: PersonSummary;
  text: string;
  stage: string;
  type: string;
}

export interface IPipelineSearchBarSuggestions {
  id: string;
  type: string;
  text: string;
  [name: string]: any;
}

export interface ICandidateSummary {
  id?: string;
  columnId?: string;
  columnCategory?: string;
  columnLastUpdated?: string;
  personToJobEval?: IPeopleEvalResult;
  jobToPersonEvalResume?: IOpportunitiesEvalResume;
  position: number;
  interested?: Date;
  retracted?: Date;
  person: IPersonSummary;
  applicationStep?: ICandidateApplicationStep;
  preMatched?: boolean;
  lastEvaluationContext?: CandidateContext;
  publicApplication: boolean;
  reviewed?: Date;
  reviewedFrom?: string;
  advisor?: ICandidateAdvisor;
  stage?: string;
  inviter?: IPersonSummary;
  compensation?: ICompensation;
  status: string;
  communityLogoUrl?: string;
  columnNameOrFunnelTag?: string;
  source?: string;
  rankValue?: number;
  maxRankValue?: number;
  matchStatus?: string;
  hasImportedResume?: boolean;
  deliveryDeadline?: CandidateDeliveryDeadline;
  isHidden?: boolean;
  hiddenUntil?: string;
  utmSource?: string;
  utmMedium?: string;
}

export class CandidateSummary  {
  public id?: string;
  public columnId?: string;
  public columnCategory?: string;
  public columnLastUpdated?: Date;
  public personToJobEval?: IPeopleEvalResult;
  public jobToPersonEvalResume?: IOpportunitiesEvalResume;
  public position: number;
  public interested?: Date;
  public retracted?: Date;
  public person: PersonSummary;
  public applicationStep?: ICandidateApplicationStep;
  public preMatched?: boolean;
  public lastEvaluationContext?: CandidateContext;
  public publicApplication: boolean;
  public reviewed?: Date;
  public reviewedFrom?: string;
  public advisor?: CandidateAdvisor;
  public stage?: string;
  public inviter?: PersonSummary;
  public compensation?: Compensation;
  public status: string;
  public communityLogoUrl?: string;
  public size?: number;
  public columnNameOrFunnelTag?: string;
  public source?: string;
  public rankValue?: number;
  public maxRankValue?: number;
  public matchStatus?: string;
  public hasImportedResume?: boolean;
  public deliveryDeadline?: CandidateDeliveryDeadline;
  public isHidden?: boolean;
  public hiddenUntil?: Date;
  public utmSource?: string;
  public utmMedium?: string;

  constructor(data: ICandidateSummary) {
    this.id = data.id;
    this.columnId = data.columnId;
    this.columnCategory = data.columnCategory;
    this.columnLastUpdated = data.columnLastUpdated ? new Date(data.columnLastUpdated) : undefined;
    this.personToJobEval = data.personToJobEval;
    this.jobToPersonEvalResume = data.jobToPersonEvalResume;
    this.position = data.position;
    this.interested = data.interested;
    this.retracted = data.retracted;
    this.person = new PersonSummary(data.person, true);
    if (data.applicationStep !== undefined) {
      this.applicationStep = typeof data.applicationStep === "string" ? JSON.parse(data.applicationStep) : data.applicationStep;
    } else {
      this.applicationStep = {} as ICandidateApplicationStep;
    }
    this.preMatched = data.preMatched;
    this.lastEvaluationContext = data.lastEvaluationContext;
    this.publicApplication = data.publicApplication;
    this.reviewed = data.reviewed;
    this.reviewedFrom = data.reviewedFrom;
    this.advisor = data.advisor ? new CandidateAdvisor(data.advisor) : undefined;
    this.stage = data.stage;
    this.compensation = data.compensation ? new Compensation(data.compensation) : undefined;
    this.status = data.status;
    if (data.inviter !== undefined) {
      this.inviter = new PersonSummary(data.inviter);
    }
    this.communityLogoUrl = data.communityLogoUrl;
    this.columnNameOrFunnelTag = data.columnNameOrFunnelTag ? data.columnNameOrFunnelTag : undefined;
    this.source = data.source;
    this.rankValue = data.rankValue;
    this.maxRankValue = data.maxRankValue;
    this.matchStatus = data.matchStatus;
    this.hasImportedResume = data.hasImportedResume;
    this.deliveryDeadline = data.deliveryDeadline ? new CandidateDeliveryDeadline(data.deliveryDeadline) : undefined;
    this.isHidden = data.isHidden;
    this.hiddenUntil = data.hiddenUntil ? new Date(data.hiddenUntil) : undefined;
    this.utmSource = data.utmSource;
    this.utmMedium = data.utmMedium;
  }
}
